import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ShieldCheck, Scale, TrendingUp, CheckCircle2, ArrowUpRight
} from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Marquee from "@/components/Marquee";
import StatCounter from "@/components/StatCounter";
import CtaBand from "@/components/CtaBand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { services, values, stats, faqs, heroImages, contactInfo } from "@/data/content";
import { MapPin, Mail, Phone } from "lucide-react";

const heroChips = [
  { icon: ShieldCheck, title: "Autorizzazioni di legge", sub: "Art. 115 T.U.L.P.S." },
  { icon: Scale, title: "Recupero integrato", sub: "Stragiudiziale & giudiziale" },
  { icon: TrendingUp, title: "Valorizzazione", sub: "Servicing & Trading" },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden bg-navy">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src={heroImages.home} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/70" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-36 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-5 py-2 backdrop-blur-md"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/85">
              Gestione Crediti Deteriorati NPL & UTP
            </span>
          </motion.div>

          <h1 className="mt-8 max-w-4xl font-display text-5xl leading-[1.03] text-white sm:text-6xl lg:text-7xl">
            <MaskedLines
              delay={0.3}
              lines={[
                "Competenza, esperienza",
                <>e <span className="italic text-gold">innovazione</span></>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
          >
            Vobis Credit S.r.l. è specializzata nell'acquisto, nella gestione e nella
            valorizzazione di portafogli di crediti deteriorati (NPL) e UTP, al servizio di
            banche, intermediari finanziari, investitori e imprese.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-navy shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
            >
              Contattaci
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/servizi"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-gold hover:text-gold"
            >
              Scopri i Servizi
            </Link>
          </motion.div>

          <div className="mt-16 grid max-w-3xl gap-4 sm:grid-cols-3">
            {heroChips.map((chip, i) => (
              <motion.div
                key={chip.title}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.05 + i * 0.13 }}
                data-testid={`hero-chip-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-md transition-colors duration-300 hover:border-gold/40"
              >
                <chip.icon size={20} className="text-gold" />
                <div className="mt-3 text-sm font-semibold text-white">{chip.title}</div>
                <div className="mt-1 text-xs text-white/50">{chip.sub}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex">
          <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-white/40">Scorri</span>
          <div className="h-14 w-px overflow-hidden bg-white/10">
            <div className="animate-scroll-cue h-full w-full bg-gold" />
          </div>
        </div>
      </section>

      <Marquee />

      {/* CHI SIAMO BREVE */}
      <section className="bg-cream py-28" data-testid="home-chi-siamo">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal className="relative">
            <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/10">
              <img
                src={heroImages.chiSiamo}
                alt="Il team Vobis Credit al lavoro"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 rounded-2xl border border-gold/30 bg-white px-7 py-5 shadow-xl shadow-navy/10 sm:right-8">
              <div className="font-display text-2xl text-navy">NPL & UTP</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">Secured & Unsecured</div>
            </div>
          </Reveal>

          <div>
            <SectionHeading
              align="left"
              eyebrow="Chi Siamo"
              title={<>Un partner specializzato nella gestione dei <span className="italic text-gold-dark">crediti deteriorati</span></>}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 leading-relaxed text-navy/65">
                Vobis Credit S.r.l. opera nell'acquisto, nella gestione e nella valorizzazione di
                portafogli di crediti deteriorati (NPL – Non Performing Loans) e UTP (Unlikely To Pay),
                nel rispetto della normativa vigente e delle autorizzazioni previste dalla legge.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Autorizzazioni ex art. 115 T.U.L.P.S.",
                  "Conformità agli artt. 114 e ss. T.U.B.",
                  "Competenze finanziarie, legali e commerciali",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm font-medium text-navy/80">
                    <CheckCircle2 size={19} className="shrink-0 text-gold-dark" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/chi-siamo"
                data-testid="home-chi-siamo-link"
                className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
              >
                Scopri la nostra storia
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVIZI PREVIEW */}
      <section className="bg-white py-28" data-testid="home-servizi">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Cosa Facciamo"
            title="Un servizio integrato lungo l'intero ciclo di vita del credito"
            description="Dall'analisi dei portafogli fino al recupero e alla valorizzazione degli asset: un unico partner per banche, intermediari finanziari, investitori e imprese."
          />
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {services.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.12}>
                <Link
                  to="/servizi"
                  data-testid={`home-service-card-${s.id}`}
                  className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-gold/50 hover:shadow-2xl hover:shadow-navy/10"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-navy">
                    <s.icon size={24} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-6 text-xl font-bold text-navy">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-navy/60">{s.short}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold-dark">
                    Scopri di più
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link
              to="/servizi"
              data-testid="home-servizi-all"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-navy/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-navy-light"
            >
              Scopri tutti i servizi
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PERCHÉ SCEGLIERE VOBIS */}
      <section className="bg-cream py-28" data-testid="home-perche-noi">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Perché Vobis Credit"
            title="Un approccio che unisce rigore, rapidità e trasparenza"
          />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div
                  data-testid={`home-value-${i}`}
                  className="group h-full rounded-3xl bg-white p-8 text-center shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-navy/10"
                >
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold transition-all duration-500 group-hover:rotate-6 group-hover:bg-gold group-hover:text-navy">
                    <v.icon size={24} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-bold text-navy">{v.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-navy/55">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link
              to="/perche-noi"
              data-testid="home-perche-noi-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
            >
              Tutti i motivi per sceglierci
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NUMERI */}
      <section className="relative overflow-hidden bg-navy py-24" data-testid="home-numeri">
        <div className="pointer-events-none absolute right-0 top-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading dark eyebrow="I Numeri" title="La solidità di un operatore specializzato" />
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s) => (
              <StatCounter key={s.label} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CtaBand tone="gold" />

      {/* FAQ BREVE */}
      <section className="bg-white py-28" data-testid="home-faq">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Domande Frequenti"
            title="Le risposte che cerchi"
          />
          <Reveal className="mt-14">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 4).map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`home-faq-${i}`}
                  data-testid={`home-faq-item-${i}`}
                  className="rounded-2xl border border-navy/10 bg-white px-6 transition-all duration-300 data-[state=open]:border-gold/60 data-[state=open]:shadow-lg data-[state=open]:shadow-gold/5"
                >
                  <AccordionTrigger className="py-5 text-left font-semibold text-navy hover:text-gold-dark hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 leading-relaxed text-navy/60">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal className="mt-10 text-center">
            <Link
              to="/faq"
              data-testid="home-faq-all"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
            >
              Vedi tutte le domande frequenti
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CONTATTI */}
      <section className="bg-cream py-28" data-testid="home-contatti">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Contatti"
              title={<>Parliamone <span className="italic text-gold-dark">insieme</span></>}
              description="Vuoi valorizzare i tuoi portafogli di crediti deteriorati? Il nostro team è a disposizione per analizzare le tue esigenze e offrirti soluzioni su misura."
            />
            <Reveal delay={0.15}>
              <ul className="mt-9 space-y-5">
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-gold-dark shadow-sm"><MapPin size={19} /></span>
                  <span className="text-sm text-navy/75">{contactInfo.offices[0].address}</span>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-gold-dark shadow-sm"><Mail size={19} /></span>
                  <a href={`mailto:${contactInfo.email}`} className="text-sm text-navy/75 transition-colors hover:text-gold-dark">{contactInfo.email}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-gold-dark shadow-sm"><Phone size={19} /></span>
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} className="text-sm text-navy/75 transition-colors hover:text-gold-dark">{contactInfo.phone}</a>
                </li>
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-[2rem] bg-navy p-10 lg:p-12">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gold/15 blur-3xl" />
              <h3 className="relative font-display text-3xl text-white lg:text-4xl">Richiedi una consulenza riservata</h3>
              <p className="relative mt-4 text-sm leading-relaxed text-white/60">
                Compila il form dedicato: ti ricontatteremo entro 24 ore lavorative per fissare un primo confronto, senza impegno.
              </p>
              <Link
                to="/contatti"
                data-testid="home-contatti-cta"
                className="group relative mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-navy shadow-xl shadow-gold/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
              >
                Vai al form di contatto
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
